import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { City, getCityBySlug } from '../config/cities';

interface CityContextValue {
  city: City | null;
  citySlug: string | null;
  isPrequalPage: boolean;
  isCityPage: boolean;
}

const CityContext = createContext<CityContextValue>({
  city: null,
  citySlug: null,
  isPrequalPage: false,
  isCityPage: false
});

export function CityProvider({ children }: { children: ReactNode }) {
  const params = useParams();
  const location = useLocation();

  const contextValue = useMemo(() => {
    // Check if we're on a prequalification page (e.g., /miami-prequalification)
    const prequalMatch = location.pathname.match(/^\/([a-z-]+)-prequalification/);
    const isPrequalPage = !!prequalMatch;
    const prequalCitySlug = prequalMatch?.[1] || null;

    // Check if we're on a city hub page (e.g., /cities/miami)
    const cityHubMatch = location.pathname.match(/^\/cities\/([a-z-]+)/);
    const cityHubSlug = cityHubMatch?.[1] || null;

    // Check if we're on a city funding page (e.g., /business-funding/miami)
    const citySlug = params.citySlug || params.city || cityHubSlug || prequalCitySlug;
    const isCityPage = (location.pathname.includes('/business-funding/') || location.pathname.includes('/cities/')) && !!citySlug;

    const city = citySlug ? getCityBySlug(citySlug) : null;

    return {
      city,
      citySlug,
      isPrequalPage,
      isCityPage
    };
  }, [params.citySlug, params.city, location.pathname]);

  return (
    <CityContext.Provider value={contextValue}>
      {children}
    </CityContext.Provider>
  );
}

export function useCityContext() {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCityContext must be used within CityProvider');
  }
  return context;
}
